/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback, useMemo, useState } from 'react';
import { PropertiesService } from '@services/requests';
import { useAgency } from '@/pages/authenticated/agency-pages/AgenciesContext';
import type { SearchAutocompleteOption } from '@/shared/ui/components/form/SearchAutocomplete';

export interface PropertyBrokerAssignment {
  memberOptions: SearchAutocompleteOption[];
  onAssign: (email: string) => Promise<void>;
  onRemove: (email: string) => Promise<void>;
  saving: boolean;
}

/**
 * Assign / remove responsible brokers on a property via PUT assigned_brokers.
 */
export function usePropertyBrokerAssignment(
  agencyId: string,
  propertyId: string,
  brokers: string[],
  onSaved: () => void,
): PropertyBrokerAssignment {
  const members = useAgency((s) => s.agency?.members) ?? [];
  const [saving, setSaving] = useState(false);

  const memberOptions = useMemo(
    () => members.map((m) => {
      const fullName = [m.first_name, m.last_name].filter(Boolean).join(' ').trim();
      return {
        id: m.email,
        label: fullName || m.email,
        sublabel: m.email,
      };
    }),
    [members],
  );

  const persist = useCallback(async (next: string[]) => {
    setSaving(true);
    try {
      await PropertiesService.updateProperty(propertyId, { assignment: { assigned_brokers: next } }, agencyId);
      onSaved();
    } finally {
      setSaving(false);
    }
  }, [agencyId, propertyId, onSaved]);

  const onAssign = useCallback(async (email: string) => {
    if (brokers.includes(email)) return;
    await persist([...brokers, email]);
  }, [brokers, persist]);

  const onRemove = useCallback(async (email: string) => {
    await persist(brokers.filter((b) => b !== email));
  }, [brokers, persist]);

  return { memberOptions, onAssign, onRemove, saving };
}
